import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import CampPopup from '../components/CampPopup';
import { optimizeImage } from '../utils/cloudinary';

const AerialPage = () => {
  // Optimized images (no cropping, just compression + auto-format)
  const images = {
    camps: optimizeImage('https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/ocka5nud_Aerial1_WeekCamp%20%281200%20x%20675%20px%29.png', { width: 600 }),
    classes: optimizeImage('https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/n908ywnr_Aerial2Classes%26Workshops%20%281200%20x%20675%20px%29.png', { width: 600 }),
    parties: optimizeImage('https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/r0esykzf_Aerial3Group_Pyramid_iFLY%20%281200%20x%20675%20px%29.png', { width: 600 })
  };

  // YouTube video ID for embedding
  const campVideoId = 'HoCb9TggjVQ';

  // Intersession camp promo auto-expires after Oct 1, 2026 11:59pm
  const showIntersession = new Date() <= new Date('2026-10-01T23:59:59');

  // Break camps calendar iframe auto-height (camp-date taps are handled inside camps.html,
  // which navigates the top window to /book-aerial with ?date= and &school= for prefill)
  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin === window.location.origin && e.data && e.data.gcfHeight) {
        const frame = document.getElementById('camps-frame');
        if (frame) frame.style.height = e.data.gcfHeight + 'px';
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return (
    <div className="service-page">
      <CampPopup />
      <div className="service-hero">
        <div className="service-hero-content">
          <div className="service-badge">Play</div>
          <h1 className="service-page-title">Aerial Kids</h1>
          <p className="service-page-subtitle">
            Where kids come to fly and leave knowing they can. Screens stay home — silks, hammocks, hoops and bungee build strength, courage and memories that stick.
          </p>
          <a href="#break-camps" className="break-camps-jump" data-testid="break-camps-jump-link">
            Need coverage on a school day off? See break camps →
          </a>
        </div>
      </div>

      <div className="service-content">
        {/* SEASONAL HIGHLIGHT - Weekly Classes */}
        <section className="seasonal-highlight aerial-classes" data-testid="aerial-announcement-box">
          <div className="seasonal-badge">🎉 NOW ENROLLING</div>
          <h2 className="seasonal-title">Weekly Aerial Classes</h2>
          <p className="seasonal-tagline">Their favorite hour of the week, all school year long.</p>
          
          <div className="seasonal-content">
            <div className="seasonal-video">
              <iframe
                src={`https://www.youtube.com/embed/${campVideoId}?autoplay=1&mute=1&loop=1&playlist=${campVideoId}&controls=0&showinfo=0&rel=0&modestbranding=1`}
                title="Aerial Kids Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            
            <div className="seasonal-details">
              <div className="camp-info-grid aerial-info-grid">
                <div className="camp-info-item">
                  <span className="camp-label">Ages</span>
                  <span className="camp-value">8 – 12</span>
                </div>
                <div className="camp-info-item">
                  <span className="camp-label">Tuition</span>
                  <span className="camp-value">$125/mo</span>
                </div>
              </div>
              
              <div className="camp-dates">
                <strong>Pick your day:</strong>
                <div className="day-options">
                  <Link to="/book-aerial" className="day-option" data-testid="class-option-wed">
                    <span className="day-name">Wednesdays</span>
                    <span className="day-times">5–6p or 6–7p</span>
                  </Link>
                  <Link to="/book-aerial" className="day-option" data-testid="class-option-thurs">
                    <span className="day-name">Thursdays</span>
                    <span className="day-times">3:15–4:15p or 4:15–5:15p</span>
                  </Link>
                </div>
              </div>
              
              <p className="camp-location">
                <strong>Location:</strong> Home Studio off Masonboro Loop Rd <em>(aka The Aerial Arena 🙂)</em>
              </p>
              
              <Link to="/book-aerial" className="seasonal-cta">
                <Button size="lg" className="register-btn" data-testid="weekly-classes-register-btn">
                  Register Today
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Weekly Classes Description */}
        <section className="bingo-callout" data-testid="weekly-classes-description">
          <div className="bingo-callout-content">
            <h3 className="bingo-callout-heading">What we fly each week 🧡</h3>
            <p className="bingo-callout-text">
              In weekly classes we explore aerial slings (hammocks), Lyra (hoop), silks, aerial pole & bungees — following a progressive curriculum. <strong>Intro</strong> is for absolute beginners with no aerial experience. <strong>Level 1 & 2</strong> placement is based on aerial, gymnastics, dance or athletic experience.
            </p>
            <a href="mailto:hello@gocalfit.com?subject=Aerial%20Kids%20—%20Where%20should%20we%20start%3F" className="bingo-callout-link" data-testid="connect-coach-cal-link">
              Not sure where to start? Connect with Coach Cal →
            </a>
          </div>
        </section>

        {/* INTERSESSION CAMPS — auto-hides after 10/1/26 11:59pm */}
        {showIntersession && (
        <section className="intersession-banner" data-testid="intersession-camps-section">
          <span className="intersession-badge">☀️ Intersession Camps</span>
          <h2 className="intersession-title">Aerial Adventure Intersession Camps</h2>
          <div className="intersession-facts">
            <span className="intersession-fact" data-testid="intersession-hours">8:00am – 3:00pm</span>
            <span className="intersession-fact-divider">·</span>
            <span className="intersession-fact" data-testid="intersession-price">$450 / week</span>
          </div>
          <div className="intersession-weeks">
            <Link to="/book-aerial" className="intersession-week" data-testid="intersession-week-1">
              Sep 28 – Oct 2, 2026
            </Link>
            <Link to="/book-aerial" className="intersession-week" data-testid="intersession-week-2">
              Oct 5 – Oct 9, 2026
            </Link>
          </div>
          <p className="intersession-lead">Some of the adventures for the week:</p>
          <ul className="intersession-list" data-testid="intersession-adventures-list">
            <li><Link to="/book-aerial">Aerial skills — silks, hammock, Lyra hoop & fan dance</Link></li>
            <li><Link to="/book-aerial">Trail days at Carolina Beach State Park</Link></li>
            <li><Link to="/book-aerial">Volleyball, pickleball, soccer & frisbee</Link></li>
            <li><Link to="/book-aerial">Wrightsville Beach paddleboarding</Link></li>
            <li><Link to="/book-aerial">Field trip to Off the Wall</Link></li>
            <li><Link to="/book-aerial">Beach days & pool days</Link></li>
            <li><Link to="/book-aerial">Time at the park</Link></li>
          </ul>
          <Link to="/book-aerial">
            <Button className="intersession-cta" data-testid="intersession-book-btn">
              Book a Camp Week →
            </Button>
          </Link>
        </section>
        )}

        {/* BREAK CAMPS CALENDAR */}
        <section id="break-camps" className="break-camps-section" data-testid="break-camps-section">
          <h2 className="dp-title">No school? Break camps are on.</h2>
          <p className="dp-sub">Camps for kids ages 8 to 11 on school days off. Pick your school, then tap a green date.</p>
          <iframe
            id="camps-frame"
            src="/api/public/files/camps-html"
            title="Break camp calendar"
            style={{ width: '100%', height: '1250px', border: 0, display: 'block' }}
            loading="lazy"
            data-testid="camps-frame"
          />
        </section>

        <div className="features-intro" data-testid="features-intro">
          <h2 className="dp-title">Ways to Fly</h2>
          <p className="dp-sub">Camps, classes and celebrations — all in the air.</p>
        </div>
        <section className="service-features">
          <Link to="/book-aerial" className="feature-card-link">
            <div className="feature-card">
              <img 
                src={images.camps}
                alt="Week-Long Camps"
                className="feature-card-image"
              />
              <div className="feature-card-content">
                <h3>Week-Long Camps</h3>
                <p className="feature-card-tagline">The camp they'll beg to go back to.</p>
                <p>Spring Break + Summer escapes that actually tire them out.</p>
              </div>
            </div>
          </Link>
          <Link to="/book-aerial" className="feature-card-link">
            <div className="feature-card">
              <img 
                src={images.classes}
                alt="Classes & Workshops"
                className="feature-card-image"
              />
              <div className="feature-card-content">
                <h3>Classes & Workshops</h3>
                <p className="feature-card-tagline">Weekly flight training for every level.</p>
                <p>Building strength, skills and friendships one class at a time.</p>
              </div>
            </div>
          </Link>
          <Link to="/book-aerial" className="feature-card-link">
            <div className="feature-card">
              <img 
                src={images.parties}
                alt="Birthday Parties"
                className="feature-card-image"
              />
              <div className="feature-card-content">
                <h3>Birthday Parties</h3>
                <p className="feature-card-tagline">High-flying celebrations they'll never forget.</p>
                <p>Because bounce houses are so last year.</p>
              </div>
            </div>
          </Link>
        </section>

        {/* DEVELOPING PROGRAMS — two-card section */}
        <section className="developing-programs" data-testid="developing-programs-section">
          <h2 className="dp-title">Developing Programs</h2>
          <p className="dp-sub">What we're building next — get in early.</p>
          <div className="dp-grid">
            <div className="dp-card dp-card-dark" data-testid="dp-card-air-academy">
              <span className="dp-badge dp-badge-enrolling">Enrolling Founding Team</span>
              <h3 className="dp-card-title">Air Academy</h3>
              <p className="dp-card-hook">What happens when aerial meets flight?</p>
              <p className="dp-card-text">
                A new youth sport combining aerial arts + weekly tunnel flying at iFLY Wilmington — limited to a founding team of six athletes. Beginners welcome, and encouraged.
              </p>
              <p className="dp-card-facts">Ages 8–12 · Tuesdays · Fall/Winter 2026</p>
              <Link to="/air-academy">
                <Button className="dp-card-cta" data-testid="explore-air-academy-btn">
                  Explore Air Academy →
                </Button>
              </Link>
            </div>
            <div className="dp-card dp-card-light" data-testid="dp-card-youth">
              <span className="dp-badge dp-badge-developing">In Development — Join the Interest List</span>
              <h3 className="dp-card-title">How do we teach our kids resilience? <em>Easy. We don't.</em></h3>
              <p className="dp-card-text">
                We create the environment for them to learn it themselves — a place where kids try things, struggle,
                ask questions, succeed, and discover what they're capable of. Through athletics, arts, agriculture & nature,
                and age-appropriate AI.
              </p>
              <p className="dp-card-facts">Exploring daytime homeschool + after-school options</p>
              <Link to="/youth">
                <Button className="dp-card-cta dp-card-cta-orange" data-testid="explore-youth-btn">
                  Help Us Build It →
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <section className="team-section">
          <h2 className="team-title">The Team</h2>
          <p className="team-subtitle">You're in good hands. Meet our coaches:</p>
          
          <div className="team-grid">
            <div className="team-member">
              <div className="team-photo">
                <img src="/images/team/cal_cutler.jpg" alt="Coach Cal" />
              </div>
              <h3 className="team-name">Coach Cal</h3>
              <p className="team-role">Founder. Energy Generator. Chief Flying Officer 😆</p>
            </div>
            
            <div className="team-member">
              <div className="team-photo">
                <img src="/images/team/jenna_houck.png" alt="Coach Jenna" />
              </div>
              <h3 className="team-name">Coach Jenna</h3>
              <p className="team-role">A decade in the air and here to help every body learn, grow, and play.</p>
            </div>
            
            <div className="team-member" data-testid="team-member-dana">
              <div className="team-photo dana-photo">
                <img src="https://customer-assets-0z36b82j.emergentagent.net/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/drw1u3ul_CoachDana1.webp" alt="Coach Dana on aerial silks" />
              </div>
              <h3 className="team-name">Coach Dana</h3>
              <p className="team-role">Over a decade on silks — thorough, endlessly patient, and the bridge young aerialists cross toward their aerial dreams.</p>
            </div>
            
            <div className="team-member">
              <div className="team-photo">
                <img src="/images/team/zel_foz.jpg" alt="Coach Zel" />
              </div>
              <h3 className="team-name">Coach Zel</h3>
              <p className="team-role">Dance-meets-aerial energy bringing culture, creativity, and joy into every kid's flight.</p>
            </div>
            
            <div className="team-member">
              <div className="team-photo mandy-photo">
                <img src="/images/team/mandy_fogg.png" alt="Coach Mandy" />
              </div>
              <h3 className="team-name">Coach Mandy</h3>
              <p className="team-role">Proof that one brave step can change everything—now helping kids find strength, balance, and belief in the air.</p>
            </div>
            
            <div className="team-member">
              <div className="team-photo honey-bear-profile">
                <img src="https://customer-assets.emergentagent.com/job_57c0eb9c-3961-4c7d-95b0-bd77dac9c429/artifacts/m6o1eppc_Honey%20Bears.png" alt="Coach Sonya - Honey Bear" />
              </div>
              <h3 className="team-name">Coach Sonya</h3>
              <p className="team-role">Bringing warmth, patience, and encouragement to every young flyer.</p>
            </div>
          </div>
          
          <div className="faq-redirect team-faq-redirect">
            <p>First time? We've got you covered. <Link to="/faq" className="faq-link">Read the FAQs →</Link></p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AerialPage;
